import { SyntheticEvent, useState } from 'react';

import {
  capitalize,
  Tabs as MuiTabs,
  TabsProps as MuiTabsProps,
} from '@mui/material';
import clsx from 'clsx';

import { InternalTabsContext } from './InternalTabsContext';
import { useTabsState } from './useTabsState';

type TabsVariant = 'standard' | 'noUnderline' | 'card' | 'scrollable';
type TabsSize = 'small' | 'medium';

export type TabsProps<Value extends string = string> = Omit<
  MuiTabsProps,
  'variant' | 'value' | 'color'
> & {
  variant?: TabsVariant;
  fullWidth?: boolean;
  size?: TabsSize;
  testId?: string;
  value?: Value;
};

export const Tabs = <Value extends string>({
  children,
  className,
  variant = 'standard',
  size = 'medium',
  fullWidth,
  testId = 'tabs',
  ...rest
}: TabsProps<Value>) => {
  const context = useTabsState<Value>();
  const [tabValueWithHover, setTabValueWithHover] = useState<Value | undefined>(
    undefined
  );
  const activeTab = rest.value || context?.currentTab;

  const onChange = (event: SyntheticEvent, value: Value) => {
    rest.onChange?.(event, value);
    if (!event.defaultPrevented) {
      context?.setCurrentTab(value);
    }
  };

  const activeTabHover = tabValueWithHover === activeTab;

  return (
    <InternalTabsContext.Provider
      value={{
        onTabMouseEnter: setTabValueWithHover,
        onTabMouseLeave: () => setTabValueWithHover(undefined),
      }}
    >
      <MuiTabs
        visibleScrollbar={false}
        data-testid={testId}
        {...rest}
        variant={variant === 'scrollable' ? 'scrollable' : undefined}
        className={clsx(
          'DS-Tabs-root',
          `DS-Tabs-variant${capitalize(variant)}`,
          `DS-Tabs-size${capitalize(size)}`,
          activeTabHover && 'DS-Tabs-activeTabHover',
          fullWidth && 'DS-Tabs-fullWidth',
          className
        )}
        onChange={onChange}
        value={activeTab}
      >
        {children}
      </MuiTabs>
    </InternalTabsContext.Provider>
  );
};
